import React from 'react'
import { router, usePage, useForm } from '@inertiajs/react'
import {
  Box, Button, TextField, Select, MenuItem, FormControl, InputLabel,
  Paper, Typography, Grid,
} from '@mui/material'
import AppShell from '../../../../../components/layout/AppShell'
import PageHeader from '../../../../../components/ui/PageHeader'

New.layout = (page) => <AppShell>{page}</AppShell>

export default function New({ property, assembly, agenda_items, resolution, errors: serverErrors = {} }) {
  const { props } = usePage()
  const tenant = props.tenant || {}
  const urlParams = new URLSearchParams(usePage().url.split('?')[1])
  const defaultAgendaId = urlParams.get('agenda_item_id')

  const { data, setData, post, processing, errors } = useForm({
    agenda_item_id: resolution.agenda_item_id || defaultAgendaId || '',
    title: resolution.title || '',
    resolution_text: resolution.resolution_text || '',
    date: resolution.date || assembly.date_iso || '',
    status: resolution.status || 'accepted',
    voting_method: resolution.voting_method || 'head_count',
    majority_requirement: resolution.majority_requirement || 'simple_majority',
    votes_for: resolution.votes_for || '',
    votes_against: resolution.votes_against || '',
    abstentions: resolution.abstentions || '',
    challenge_deadline: resolution.challenge_deadline || '',
  })

  const allErrors = { ...serverErrors, ...errors }

  const handleSubmit = (e) => {
    e.preventDefault()
    post(`/m/${tenant.slug}/properties/${property.id}/assemblies/${assembly.id}/resolutions`)
  }

  return (
    <Box>
      <PageHeader
        title="Beschluss erfassen"
        breadcrumbs={[
          { label: 'Versammlungen', href: `/m/${tenant.slug}/properties/${property.id}/assemblies` },
          { label: assembly.date, href: `/m/${tenant.slug}/properties/${property.id}/assemblies/${assembly.id}` },
          { label: 'Beschluss' },
        ]}
      />

      <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider', maxWidth: 700 }}>
        <Box component="form" onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <FormControl fullWidth size="small" required error={!!allErrors.agenda_item_id}>
                <InputLabel>Tagesordnungspunkt</InputLabel>
                <Select value={data.agenda_item_id} label="Tagesordnungspunkt"
                  onChange={(e) => setData('agenda_item_id', e.target.value)}
                  data-testid="agenda-item-select">
                  {agenda_items.map((item) => (
                    <MenuItem key={item.id} value={item.id}>TOP {item.position}: {item.title}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField label="Datum" type="date" required fullWidth size="small"
                value={data.date} onChange={(e) => setData('date', e.target.value)}
                slotProps={{ inputLabel: { shrink: true } }} />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField label="Titel" required fullWidth size="small"
                value={data.title} onChange={(e) => setData('title', e.target.value)}
                error={!!allErrors.title} helperText={allErrors.title?.[0]}
                slotProps={{ htmlInput: { 'data-testid': 'title-input' } }} />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField label="Beschlusstext" required fullWidth size="small" multiline rows={4}
                value={data.resolution_text} onChange={(e) => setData('resolution_text', e.target.value)}
                error={!!allErrors.resolution_text} helperText={allErrors.resolution_text?.[0]}
                slotProps={{ htmlInput: { 'data-testid': 'resolution-text-input' } }} />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select value={data.status} label="Status"
                  onChange={(e) => setData('status', e.target.value)}>
                  <MenuItem value="accepted">Angenommen</MenuItem>
                  <MenuItem value="rejected">Abgelehnt</MenuItem>
                  <MenuItem value="deferred">Vertagt</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Abstimmungsart</InputLabel>
                <Select value={data.voting_method} label="Abstimmungsart"
                  onChange={(e) => setData('voting_method', e.target.value)}>
                  <MenuItem value="head_count">Kopfprinzip</MenuItem>
                  <MenuItem value="value_based">Wertprinzip</MenuItem>
                  <MenuItem value="unit_based">Einheitsprinzip</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Mehrheitserfordernis</InputLabel>
                <Select value={data.majority_requirement} label="Mehrheitserfordernis"
                  onChange={(e) => setData('majority_requirement', e.target.value)}>
                  <MenuItem value="simple_majority">Einfache Mehrheit</MenuItem>
                  <MenuItem value="qualified_majority">Qualifizierte Mehrheit</MenuItem>
                  <MenuItem value="unanimity">Einstimmigkeit</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField label="Ja-Stimmen" type="number" fullWidth size="small"
                value={data.votes_for} onChange={(e) => setData('votes_for', e.target.value)}
                slotProps={{ htmlInput: { 'data-testid': 'votes-for-input', min: 0 } }} />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField label="Nein-Stimmen" type="number" fullWidth size="small"
                value={data.votes_against} onChange={(e) => setData('votes_against', e.target.value)} />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField label="Enthaltungen" type="number" fullWidth size="small"
                value={data.abstentions} onChange={(e) => setData('abstentions', e.target.value)} />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField label="Anfechtungsfrist" type="date" fullWidth size="small"
                value={data.challenge_deadline} onChange={(e) => setData('challenge_deadline', e.target.value)}
                slotProps={{ inputLabel: { shrink: true } }} />
            </Grid>
          </Grid>

          <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
            <Button type="submit" variant="contained" disabled={processing} data-testid="submit-btn">
              {processing ? 'Wird gespeichert...' : 'Beschluss erfassen'}
            </Button>
            <Button variant="outlined"
              onClick={() => router.visit(`/m/${tenant.slug}/properties/${property.id}/assemblies/${assembly.id}`)}>
              Abbrechen
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  )
}
